import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "./components/layout/Header";
import SidebarEstudiante from "./components/SidebarEstudiante";
import { Marquee } from "./components/Marquee";
import { COMPANY_LOGOS } from "./constants/companyLogos";
import { useAuth } from "./context/AuthContext";
import { authService } from "./services/authService";
import "./components/layout/MainLayout.css";
import "./Home.css";

function Home() {
  const { isAuthenticated } = useAuth();
  const [selectedOption, setSelectedOption] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [registrationsEnabled, setRegistrationsEnabled] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        const data = await authService.getRegistrationsSetting();
        setRegistrationsEnabled(!!data.registrations_enabled);
      } catch {
        setRegistrationsEnabled(true);
      }
    };
    check();
  }, []);

  const handleOptionClick = (option) => {
    if (!hasAnswered) {
      setSelectedOption(option);
      setHasAnswered(true);
    }
  };

  const homeMainContent = (
    <>
      <div className="home-content-wrapper">
          {/* Left Column - Text Content */}
          <div className="home-text-section">
            <h1 className="home-title">Examcat Suite</h1>
            <p className="home-description">
              A web platform for certification exam preparation. Create, edit
              and take exams that are exactly like the real ones.
            </p>

            <ul className="home-features">
              <li className="feature-item">
                <span className="feature-dot"></span>
                Realistic exam simulation
              </li>
              <li className="feature-item">
                <span className="feature-dot"></span>
                Exam editor with preview function
              </li>
              <li className="feature-item">
                <span className="feature-dot"></span>
                Different question types
              </li>
              <li className="feature-item">
                <span className="feature-dot"></span>
                All exams in one place
              </li>
              <li className="feature-item">
                <span className="feature-dot"></span>
                Customizable exam mode
              </li>
              <li className="feature-item">
                <span className="feature-dot"></span>
                Detailed scoring reports
              </li>
            </ul>

            <div className="home-action-buttons">
              <Link to="/exams" className="btn-primary">
                Take Exam
              </Link>
            </div>
          </div>

          {/* Right Column - Visual/Screenshots */}
          <div className="home-visual-section">
            <div className="screenshot-container">
              <div className="screenshot screenshot-front">
                <div className="screenshot-header">
                  <div className="screenshot-controls">
                    <span className="control-dot"></span>
                    <span className="control-dot"></span>
                    <span className="control-dot"></span>
                  </div>
                  <span className="screenshot-title">Examcat - Take Exam</span>
                </div>
                <div className="screenshot-content">
                  {!hasAnswered ? (
                    <div className="mock-question">
                      <div className="mock-question-number">
                        Question 1 of 10
                      </div>
                      <div className="mock-question-text">
                        What is the capital of France?
                      </div>
                      <div className="mock-options">
                        <div
                          className={`mock-option ${
                            selectedOption === "A" ? "selected" : ""
                          }`}
                          onClick={() => handleOptionClick("A")}
                        >
                          A) Madrid
                        </div>
                        <div
                          className={`mock-option ${
                            selectedOption === "B" ? "selected" : ""
                          }`}
                          onClick={() => handleOptionClick("B")}
                        >
                          B) París
                        </div>
                        <div
                          className={`mock-option ${
                            selectedOption === "C" ? "selected" : ""
                          }`}
                          onClick={() => handleOptionClick("C")}
                        >
                          C) Londres
                        </div>
                        <div
                          className={`mock-option ${
                            selectedOption === "D" ? "selected" : ""
                          }`}
                          onClick={() => handleOptionClick("D")}
                        >
                          D) Berlín
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mock-registration-message">
                      <p className="registration-message-text">
                        Are you enjoying how our application works?
                      </p>
                      <p className="registration-message-text">
                        So what are you waiting for to sign up?
                      </p>
                      {registrationsEnabled && (
                        <Link to="/register" className="btn-register-mock">
                          Sign Up
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="screenshot screenshot-back">
                <div className="screenshot-header">
                  <div className="screenshot-controls">
                    <span className="control-dot"></span>
                    <span className="control-dot"></span>
                    <span className="control-dot"></span>
                  </div>
                  <span className="screenshot-title">
                    Examcat - Create Exam
                  </span>
                </div>
                <div className="screenshot-content">
                  <div className="mock-editor">
                    <div className="mock-editor-toolbar"></div>
                    <div className="mock-editor-content">
                      <div className="mock-editor-line"></div>
                      <div className="mock-editor-line short"></div>
                      <div className="mock-editor-line"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="logos-section">
          <Marquee pauseOnHover={false}>
            {COMPANY_LOGOS.map((company, index) => (
              <div key={index} className="logo-item">
                <img
                  src={company.logo}
                  alt={company.name}
                  className="company-logo-img"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <span className="company-name">{company.name}</span>
              </div>
            ))}
          </Marquee>
        </div>

        {/* About Us Section */}
        <section className="home-about-section">
          <div className="about-cards-container">
            {/* Opción 1: Enfoque en la Accesibilidad */}
            <div className="about-card">
              <div className="about-card-image">
                <img src="/images/home-img1.png" alt="Accesibilidad" />
              </div>
              <div className="about-card-content">
                <h2 className="about-card-title">
                  Examcat: Power Your Learning, Regardless of Your Profile
                </h2>
                <p className="about-card-text">
                  We designed Examcat with simplicity in mind. You don't need to
                  be a tech expert to get started; our platform is intuitive and
                  easy to use for everyone. Whether you're an engineer looking
                  to update your skills, a teacher preparing materials, a
                  university student, or simply someone learning as a hobby,
                  Examcat removes technical barriers so you can focus on what
                  matters: mastering knowledge.
                </p>
              </div>
            </div>

            {/* Opción 2: Caso de Éxito / Testimonio */}
            <div className="about-card">
              <div className="about-card-image">
                <img src="/images/home-img2.png" alt="Testimonio" />
              </div>
              <div className="about-card-content">
                <h2 className="about-card-title">
                  From Frustration to Certification: Karen's Story
                </h2>
                <p className="about-card-text">
                  Karen had tried to pass her IT certification several times
                  without success, despite studying all the recommended
                  material. The fear of failing again paralyzed her. Everything
                  changed when she started using Examcat.
                </p>
                <p className="about-card-text">
                  With Examcat, "failure" became a learning tool. Instant
                  automatic feedback showed her exactly where her mistakes were,
                  while personalized study recommendations guided her review.
                  Thanks to unlimited attempts, Karen was able to practice
                  without pressure until she transformed her anxiety into
                  confidence and, finally, achieved her certification.
                </p>
              </div>
            </div>
          </div>
        </section>
    </>
  );

  return isAuthenticated ? (
    <div className="main-layout-wrapper">
      <Header showDashboardLink={true} className="home-header-nav" />
      <div className="main-layout-container">
        <SidebarEstudiante
          onSelectOption={undefined}
          selectedOption={undefined}
        />
        <main className="main-layout-content home-main-content">
          {homeMainContent}
        </main>
      </div>
    </div>
  ) : (
    <div className="home-wrapper">
      <Header showDashboardLink={true} className="home-header-nav" />
      <main className="home-main-content">{homeMainContent}</main>
    </div>
  );
}

export default Home;
